import { z } from "zod";
import { fromMinutes, toMinutes } from "./catalog";


const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, "Use a time like 09:30");
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-12-25");
/** Local date and time in the clinic's time zone, e.g. 2026-10-08T14:30. */
const localDateTime = z.string().regex(/^\d{4}-\d{2}-\d{2}T([01]\d|2[0-3]):[0-5]\d$/, "Use a date and time");

// ── Resources ───────────────────────────────────────────────────────────

export const resourceTypeInputSchema = z.object({
  name: z.string().trim().min(1).max(60),
  description: z.string().trim().max(300).default(""),
  active: z.boolean().default(true),
});

export const resourceInputSchema = z.object({
  typeId: z.string().uuid(),
  name: z.string().trim().min(1).max(60),
  locationId: z.string().uuid().nullable().default(null),
  active: z.boolean().default(true),
});

export const resourceBlockInputSchema = z
  .object({
    /** Exactly one of resourceId (one unit) or typeId (every unit of a type). */
    resourceId: z.string().uuid().nullable().default(null),
    typeId: z.string().uuid().nullable().default(null),
    starts: localDateTime,
    ends: localDateTime,
    reason: z.string().trim().max(200).default(""),
  })
  .refine((b) => (b.resourceId === null) !== (b.typeId === null), "Pick one resource or a whole type")
  .refine((b) => b.ends > b.starts, { message: "The end must be after the start", path: ["ends"] });

export interface ResourceUnit {
  id: string;
  typeId: string;
  name: string;
  locationId: string | null;
  active: boolean;
}

export interface ResourceTypeItem {
  id: string;
  name: string;
  description: string;
  active: boolean;
  units: ResourceUnit[];
}

export interface ResourceBlock {
  id: string;
  resourceId: string | null;
  typeId: string | null;
  /** Local date-times in the clinic's time zone. */
  starts: string;
  ends: string;
  reason: string;
}

export interface ResourcesResponse {
  types: ResourceTypeItem[];
  blocks: ResourceBlock[];
}

/** What a service or add-on needs: a number of any unit of a type, or one specific unit. */
export const requirementsInputSchema = z.object({
  requirements: z
    .array(
      z
        .object({
          typeId: z.string().uuid().nullable().default(null),
          resourceId: z.string().uuid().nullable().default(null),
          quantity: z.number().int().min(1).max(20).default(1),
        })
        .refine((r) => (r.typeId === null) !== (r.resourceId === null), "Pick a type or a specific resource"),
    )
    .max(20),
});

export interface ResourceRequirement {
  typeId: string | null;
  resourceId: string | null;
  quantity: number;
}

// ── Staff availability ──────────────────────────────────────────────────

/** Hours count for clinic and mobile visits alike. */
const period = z.object({ start: hhmm, end: hhmm });
const endAfterStart = (x: { start: string; end: string }, ctx: z.RefinementCtx) => {
  if (toMinutes(x.end) <= toMinutes(x.start)) ctx.addIssue({ code: "custom", path: ["end"], message: "The end must be after the start" });
};

/** Add hours on a date: once, or every week from that date (optionally until another). */
export const addHoursSchema = period
  .extend({ date: isoDate, repeat: z.boolean().default(false), until: isoDate.nullable().default(null) })
  .superRefine((x, ctx) => {
    endAfterStart(x, ctx);
    if (x.until && x.until < x.date) ctx.addIssue({ code: "custom", path: ["until"], message: "The last week can't be before the first" });
  });
export type AddHoursInput = z.infer<typeof addHoursSchema>;

/**
 * Change or remove hours. For weekly hours, `scope` says whether it's just `date` or `date` and every week after;
 * one-day hours ignore it. `remove` takes the hours away instead of changing them.
 */
export const changeHoursSchema = period
  .extend({ date: isoDate, scope: z.enum(["day", "following"]).default("following"), remove: z.boolean().default(false) })
  .superRefine((x, ctx) => {
    if (!x.remove) endAfterStart(x, ctx);
  });
export type ChangeHoursInput = z.infer<typeof changeHoursSchema>;

/** Time off: from a date (and time) to a date (and time). No times = whole days. */
export const timeOffSchema = z
  .object({
    startDate: isoDate,
    /** Null = from the start of the day. */
    start: hhmm.nullable().default(null),
    endDate: isoDate,
    /** Null = to the end of the day. */
    end: hhmm.nullable().default(null),
    note: z.string().trim().max(200).default(""),
  })
  .superRefine((x, ctx) => {
    const a = `${x.startDate}T${x.start ?? "00:00"}`;
    const b = `${x.endDate}T${x.end ?? "24:00"}`;
    if (b <= a) ctx.addIssue({ code: "custom", path: ["end"], message: "The end must be after the start" });
    if (Date.parse(`${x.endDate}T00:00:00Z`) - Date.parse(`${x.startDate}T00:00:00Z`) > 366 * 86_400_000) ctx.addIssue({ code: "custom", path: ["endDate"], message: "Time off can be up to a year at a time" });
  });
export type TimeOffInput = z.infer<typeof timeOffSchema>;

/** Weekly hours from `startsOn` (and until `endsOn`, if set). */
export interface AvailabilityPeriod {
  id: string;
  weekday: number;
  start: string;
  end: string;
  startsOn: string;
  endsOn: string | null;
}

/** Hours on one date only. */
export interface AvailabilityExtra {
  id: string;
  date: string;
  start: string;
  end: string;
}

export interface TimeOff {
  id: string;
  startDate: string;
  /** "HH:MM"; "00:00" = from the start of the day. */
  start: string;
  endDate: string;
  /** "HH:MM"; "24:00" = to the end of the day. */
  end: string;
  /** Null when the viewer can't see it (someone else's, without "Manage everyone's availability"). */
  note: string | null;
  /** Busy times from their own calendar: the event's name, shown to that person only. */
  title?: string | null;
}

export interface StaffAvailability {
  staffId: string;
  name: string;
  color: string | null;
  periods: AvailabilityPeriod[];
  extras: AvailabilityExtra[];
  timeOff: TimeOff[];
  /** Busy on their own calendar (when they've turned that on): blocks booking like time off; changed there, not here. */
  busy?: TimeOff[];
}

/** Weekday (0 = Sunday) of an ISO date, independent of the viewer's time zone. */
export const weekdayOf = (date: string) => new Date(`${date}T12:00:00Z`).getUTCDay();

/** An ISO date plus or minus some days. */
export function shiftDate(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/** The part of a date that time off covers (minutes after midnight), or null. */
export function timeOffOn(t: Pick<TimeOff, "startDate" | "start" | "endDate" | "end">, date: string): { start: number; end: number } | null {
  if (date < t.startDate || date > t.endDate) return null;
  const start = date === t.startDate ? toMinutes(t.start) : 0;
  const end = date === t.endDate ? toMinutes(t.end) : 1440;
  return end > start ? { start, end } : null;
}

/** Weekly hours in effect on a date. */
export const periodApplies = (p: Pick<AvailabilityPeriod, "weekday" | "startsOn" | "endsOn">, date: string) =>
  p.weekday === weekdayOf(date) && p.startsOn <= date && (p.endsOn === null || p.endsOn >= date);

type Span = { start: number; end: number };
/** Take spans out of a list of periods (each keeps its other fields). */
export function cutOut<T extends Span>(list: T[], remove: Span[]): T[] {
  let out = list;
  for (const r of remove) {
    out = out.flatMap((p) => {
      if (r.end <= p.start || r.start >= p.end) return [p];
      const parts: T[] = [];
      if (r.start > p.start) parts.push({ ...p, end: r.start });
      if (r.end < p.end) parts.push({ ...p, start: r.end });
      return parts;
    });
  }
  return out;
}

/**
 * A staff member's open periods on one date: weekly hours in effect, plus one-day hours, minus time off.
 * Times are minutes after midnight in the clinic's time zone.
 */
export function availabilityOn(a: Pick<StaffAvailability, "periods" | "extras" | "timeOff">, date: string): { start: number; end: number }[] {
  const open = [
    ...a.periods.filter((p) => periodApplies(p, date)).map((p) => ({ start: toMinutes(p.start), end: toMinutes(p.end) })),
    ...a.extras.filter((x) => x.date === date).map((x) => ({ start: toMinutes(x.start), end: toMinutes(x.end) })),
  ];
  const off = a.timeOff.map((t) => timeOffOn(t, date)).filter((x): x is Span => x !== null);
  return cutOut(open, off).sort((x, y) => x.start - y.start);
}

/** Whether `[start, end)` is inside the periods (touching periods count as one). */
export function coveredBy(periods: Span[], start: number, end: number): boolean {
  let at = start;
  for (const p of [...periods].sort((x, y) => x.start - y.start)) {
    if (p.start > at) break;
    if (p.end > at) at = p.end;
    if (at >= end) return true;
  }
  return at >= end;
}

/** A nurse's booked time on a date (minutes), for checking changes to their availability. */
export interface BookedTime {
  id: string;
  date: string;
  start: number;
  end: number;
  /** Clinic and mobile visits need the nurse's hours; event visits and shifts only need them not to be off. */
  context: "clinic" | "mobile" | "event";
  label: string;
}

/**
 * Booked time a change would leave without its nurse: covered by their hours before and not after
 * (clinic and mobile), or newly inside time off (events). Things that were already uncovered don't count.
 */
export function strandedBy(before: Pick<StaffAvailability, "periods" | "extras" | "timeOff">, after: Pick<StaffAvailability, "periods" | "extras" | "timeOff">, booked: BookedTime[]): BookedTime[] {
  const off = (a: typeof before, b: BookedTime) =>
    a.timeOff.some((t) => {
      const x = timeOffOn(t, b.date);
      return x !== null && x.start < b.end && x.end > b.start;
    });
  return booked.filter((b) => {
    if (b.context === "event") return off(after, b) && !off(before, b);
    const ok = (a: typeof before) => coveredBy(availabilityOn(a, b.date), b.start, b.end);
    return ok(before) && !ok(after);
  });
}

/** The availability calendar: one week (or day) for some staff. */
export interface AvailabilityWeek {
  from: string;
  to: string;
  timezone: string;
  /** Hours the calendar shows by default (minutes). */
  viewStart: number;
  viewEnd: number;
  /** Who the viewer may change: everyone (manager) or just themselves. */
  canManage: boolean;
  canEditOwn: boolean;
  /** Sees everyone (not just themselves). */
  seesTeam: boolean;
  me: string;
  /** Who the viewer last chose to see besides themselves (saved to their account); null = not chosen yet. */
  myPeople: string[] | null;
  staff: StaffAvailability[];
  /** The clinic's open hours per date, closures taken out. */
  openHours: { date: string; periods: { start: number; end: number }[] }[];
  closures: { date: string; label: string; start: number | null; end: number | null }[];
  /** Booked visits and event shifts in the week, per nurse. */
  booked: (BookedTime & { staffId: string; appointmentId: string | null })[];
}

export const formatPeriod = (p: { start: number; end: number }) => `${fromMinutes(p.start)}–${fromMinutes(p.end)}`;
